import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AuthService } from '../../services/auth.service';

/**
 * Rol simülasyonu aktifken her sayfanın üstünde görünen uyarı bandı.
 *
 * Süper Admin oturumuna dönüşün tek yolu budur: simüle edilen rol düşük yetkiliyse
 * /sys/admin/* sayfalarına erişilemez, dolayısıyla simülasyon sayfasına geri dönülemez.
 *
 * app.component.html içinde kullanılır (_metronic klasörü değiştirilemediği için).
 */
@Component({
  selector: 'app-simulasyon-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './simulasyon-banner.component.html',
  styleUrls: ['./simulasyon-banner.component.scss'],
})
export class SimulasyonBannerComponent implements OnInit, OnDestroy {
  /** Bandın body içeriğini aşağı itmesi için body'ye eklenen sınıf. */
  private static readonly BODY_CLASS = 'simulasyon-aktif';

  gorunur = false;
  aciklama = '';
  bitiriliyor = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly authService: AuthService,
    private readonly renderer: Renderer2,
    // AppComponent OnPush; async abonelik sonuçları için değişikliği elle işaretlemek gerekiyor.
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // localStorage'daki oturum bilgisi ilk kaynak; band anında görünsün.
    this.bandiGuncelle(this.authService.isSimulasyonAktif, this.authService.simulasyonAciklama);

    if (!this.authService.isSimulasyonAktif) {
      return;
    }

    // Sunucuya doğrulat: simülasyon token'ı sunucu tarafında kapatılmış olabilir.
    this.authService
      .SimulasyonDurum()
      .pipe(takeUntil(this.destroy$))
      .subscribe((durum) => {
        const sunucudaAktif = durum?.isSimulasyon === true;

        // Sunucu "yok" diyorsa yerel işaret de temizlenmeli. Eskiden yalnız band gizleniyor,
        // localStorage'daki bayrak duruyordu; bir sonraki sayfa yüklemesinde band geri geliyor
        // ve kullanıcı bu döngüden çıkamıyordu.
        if (!sunucudaAktif) {
          this.authService.SimulasyonIsaretiniTemizle();
        }

        this.bandiGuncelle(sunucudaAktif, durum?.simulasyonAciklama ?? '');
      });
  }

  ngOnDestroy(): void {
    this.bodySinifiniKaldir();
    this.destroy$.next();
    this.destroy$.complete();
  }

  simulasyonuBitir(): void {
    if (this.bitiriliyor) {
      return;
    }

    Swal.fire({
      title: 'Süper Admin oturumuna dönülsün mü?',
      text: 'Simülasyon sonlandırılacak ve kendi yetkilerinizle devam edeceksiniz.',
      icon: 'question',
      showConfirmButton: true,
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      denyButtonColor: '#d33',
      confirmButtonText: 'Evet, dön',
      denyButtonText: 'Vazgeç',
      allowOutsideClick: false,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.bitiriliyor = true;
      this.changeDetectorRef.markForCheck();

      this.authService
        .SimulasyonBitir()
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          if (response?.kisiTokenDto?.isLogin) {
            this.bitiriliyor = false;
            this.changeDetectorRef.markForCheck();

            // Not: DB'deki RouterLink (/sys/admin/...) ile gerçek Angular URL'i (/super-admin/...)
            // farklı; buraya gerçek URL yazılmalı.
            this.authService.SimulasyonSonrasiYenile('/super-admin/rol-simulasyon');
            return;
          }

          // Bitirilemedi. Eskiden burada sessizce çıkılıyordu ve band ekranda kalıyordu:
          // sunucu "aktif simülasyon yok" derken yerel oturum simülasyon açık sanıyorsa
          // kullanıcının banttan kurtulmasının HİÇBİR yolu kalmıyordu.
          // Sebebi tahmin etmek yerine (hata metnine bakmak kırılgan) sunucuya soruyoruz.
          this.tutarsizDurumuCoz();
        });
    });
  }

  /**
   * Simülasyon bitirilemedi. Gerçekten aktif bir simülasyon var mı diye sunucuya danışır.
   *
   * - Sunucu "simülasyon yok" diyorsa: yerel işaret bayattır, temizlenir ve band kaldırılır.
   * - Sunucu "simülasyon var" diyorsa: geçici bir arıza olmuştur, band DURMALI; aksi halde
   *   kullanıcı gerçek bir simülasyonun içindeyken uyarısız kalır.
   */
  private tutarsizDurumuCoz(): void {
    this.authService
      .SimulasyonDurum()
      .pipe(takeUntil(this.destroy$))
      .subscribe((durum) => {
        this.bitiriliyor = false;

        if (durum?.isSimulasyon === true) {
          // Simülasyon hâlâ açık; bandı bırakıyoruz. Hata mesajını HttpService gösterdi.
          this.changeDetectorRef.markForCheck();
          return;
        }

        this.authService.SimulasyonIsaretiniTemizle();
        this.bandiGuncelle(false, '');

        Swal.fire({
          title: 'Simülasyon zaten kapanmış',
          text:
            'Sunucuda aktif bir simülasyon oturumu bulunmuyor. Uyarı bandı kaldırıldı; ' +
            'kendi yetkilerinizle devam ediyorsunuz.',
          icon: 'info',
          confirmButtonText: 'Tamam',
        });
      });
  }

  private bandiGuncelle(gorunur: boolean, aciklama: string): void {
    this.gorunur = gorunur;
    this.aciklama = aciklama;
    this.changeDetectorRef.markForCheck();

    if (gorunur) {
      this.renderer.addClass(document.body, SimulasyonBannerComponent.BODY_CLASS);
    } else {
      this.bodySinifiniKaldir();
    }
  }

  private bodySinifiniKaldir(): void {
    this.renderer.removeClass(document.body, SimulasyonBannerComponent.BODY_CLASS);
  }
}
